import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw } from 'lucide-react';

import { useVersionCheck } from '../hooks/useVersionCheck';

/**
 * "A new version is available."
 *
 * Two things were wrong with the old one, and together they produced a banner
 * that could never be dismissed:
 *
 * 1. It asked the BACKEND whether a new FRONTEND existed (see useVersionCheck).
 *    The backend cannot know that. Once it shipped a patch ahead, the banner
 *    was permanently true.
 *
 * 2. Refresh called `window.location.reload()`, which re-serves whatever the
 *    cache already has. Even when there genuinely IS a new build, a plain
 *    reload can hand you the same bundle back — so the user clicks, nothing
 *    changes, and the banner returns. Which is exactly what it did.
 *
 * A reload has to be told to go and get the new thing: drop the Cache Storage
 * entries, retire any service worker, and only then reload. And because the
 * shell is `max-age=0, must-revalidate` on Hosting, that reload will actually
 * pick up the new hashed bundles.
 *
 * It's also dismissible now. An update is an invitation, not a hostage
 * situation — and mid-round is the worst possible moment to be nagged.
 */
export function UpdateNotification() {
  const { newVersionAvailable, latestVersion } = useVersionCheck();
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
    } catch {
      // If we can't clear it we still reload — worst case, nothing changes,
      // which is no worse than not trying.
    }
    // Bypass the bfcache too: a fresh URL, not a re-entry of this one.
    window.location.replace(`${window.location.pathname}${window.location.search}`);
  };

  const show = newVersionAvailable && !dismissed;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ type: 'spring', stiffness: 320, damping: 32 }}
          role="status"
          style={{
            position: 'fixed',
            top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 900,
            width: 'calc(100% - 32px)',
            maxWidth: 420,
          }}
        >
          <div
            className="card"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              background: 'var(--ink)',
              color: 'var(--bg)',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <RefreshCw size={17} style={{ flex: 'none', opacity: 0.85 }} />
            <div style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
              <div style={{ fontWeight: 700, fontSize: 14.5 }}>WordRush just updated</div>
              {latestVersion && (
                <div style={{ fontSize: 12.5, opacity: 0.7, fontFamily: 'var(--mono)' }}>
                  v{latestVersion}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              style={{
                flex: 'none',
                minHeight: 36,
                padding: '0 14px',
                borderRadius: 10,
                border: 'none',
                cursor: refreshing ? 'default' : 'pointer',
                background: 'var(--accent)',
                color: '#fff',
                fontFamily: 'var(--ui)',
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              {refreshing ? 'Updating…' : 'Reload'}
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              aria-label="Not now"
              style={{
                flex: 'none',
                width: 32,
                height: 32,
                borderRadius: 8,
                border: 'none',
                background: 'transparent',
                color: 'var(--bg)',
                opacity: 0.55,
                cursor: 'pointer',
                fontSize: 17,
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
